import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';

import { styled } from '@mui/material/styles';
import { Box, Button, Tooltip } from '@mui/material';

import Iconify from 'components/Iconify';
import { IMAGE_SIZE, VIDEO_SIZE } from 'helpers/constants/files';
import RejectionFiles from './errors/RejectionFiles';
import MultiFilePreview from './preview/MultiFilePreview';

const DropZoneStyle = styled('div')(({ theme }) => ({
  outline: 'none',
  padding: theme.spacing(3, 1),
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.background.neutral,
  border: `1px dashed ${theme.palette.grey[500_32]}`,
  '&:hover': { opacity: 0.72, cursor: 'pointer' },
}));

UploadMultiFile.propTypes = {
  error: PropTypes.bool,
  showPreview: PropTypes.bool,
  imageView: PropTypes.bool,
  files: PropTypes.array,
  helperText: PropTypes.node,
  onRemove: PropTypes.func,
  onRemoveAll: PropTypes.func,
  onDownload: PropTypes.func,
  sx: PropTypes.object,
  children: PropTypes.node,
};

export default function UploadMultiFile({
  error,
  showPreview = false,
  imageView = false,
  files,
  helperText,
  onRemove,
  onRemoveAll,
  onDownload,
  sx,
  children,
  ...other
}) {
  const customValidator = (file) => {
    const fileType = file.type;

    if (fileType.startsWith('image/') && file.size > IMAGE_SIZE) {
      return {
        code: 'file-too-large',
      };
    }

    if (fileType.startsWith('video/') && file.size > VIDEO_SIZE) {
      return {
        code: 'file-too-large',
      };
    }

    return null;
  };

  const { getRootProps, getInputProps, isDragActive, isDragReject, fileRejections } = useDropzone({
    validator: customValidator,
    ...other,
  });

  const hasFiles = (files && files.length > 0) || (files && Object.keys(files).length > 0);

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <DropZoneStyle
        {...getRootProps()}
        sx={{
          ...(isDragActive && { opacity: 0.72 }),
          ...((isDragReject || error) && {
            color: 'error.main',
            borderColor: 'error.light',
            bgcolor: 'error.lighter',
          }),
        }}
      >
        <input {...getInputProps()} />
        {children}
      </DropZoneStyle>

      {fileRejections.length > 0 && <RejectionFiles fileRejections={fileRejections} />}

      {hasFiles && (
        <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
          <MultiFilePreview
            files={files}
            showPreview={showPreview}
            imageView={imageView}
            onRemove={onRemove}
            onRemoveAll={onRemoveAll}
            onDownload={onDownload}
          />

          {onRemoveAll && (
            <Tooltip title="Հեռացնել բոլորը">
              <Button
                color="inherit"
                variant="outlined"
                size="small"
                onClick={onRemoveAll}
                sx={{
                  m: 0.5,
                  mb: 2,
                  ml: 'auto',
                  width: 34,
                  height: 54,
                  borderRadius: 1.25,
                  overflow: 'hidden',
                  alignSelf: 'flex-end',
                }}
              >
                <Iconify icon="eva:close-fill" width={32} />
              </Button>
            </Tooltip>
          )}
        </Box>
      )}

      {helperText && helperText}
    </Box>
  );
}
